import { useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import type { MarginNoteBlock, Source, ThoughtLikeBlock } from '../../lib/types'
import { thoughtSourceLabel } from '../../lib/blocks'
import { noteHasLink, noteHtml, noteHtmlText, noteTextHtml } from '../../lib/sanitize'
import { insertPlainText } from './caret'
import { isLinkKey } from './insertChoices'
import { btn, inputCls, labelCls } from '../ui'

// Pieces shared by every thought-like block in the composer: the column
// thought, margin note, pin and unfold.

/** Props every selectable (non-text) block view takes. */
export interface SelectableProps {
  selected: boolean
  onSelect: () => void
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void
  onRemove: () => void
}

/** Source editing, shared by every thought-like view. */
export interface SourceProps {
  sourceFormOpen: boolean
  onEditSource: () => void
  onCloseSource: () => void
  onSaveSource: (source: Source | undefined, own: boolean) => void
}

/** A textarea that grows with its text, for the author's own words in a note. */
export function AutoTextarea({
  value,
  onChange,
  className = '',
  label,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  className?: string
  label: string
  placeholder?: string
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [value])
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      aria-label={label}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      className={`block w-full resize-none overflow-hidden bg-transparent p-0 outline-none placeholder:text-muted-foreground/70 ${className}`}
    />
  )
}

/** A thought's words: editable when they're the author's own, quoted as saved otherwise. */
export function ThoughtText({
  block,
  className,
  onChange,
}: {
  block: ThoughtLikeBlock
  className: string
  onChange: (text: string) => void
}) {
  if (block.own) return <AutoTextarea value={block.text} onChange={onChange} label="Note text" placeholder="Your words…" className={className} />
  return <p className={`m-0 ${className}`}>{block.text}</p>
}

/**
 * A margin note's words, which can carry links (Niki, 10/5): select some and
 * the selection toolbar offers Link, as in a paragraph, or press ⌘K / Ctrl+K.
 * The author's own words are typed here as plain text (no formatting, pasting
 * as plain text, Enter for a new line); a thought's words stay as saved but
 * can still be linked. The composer finds it by `data-note-text` and saves
 * what changes through `onChange` (text, plus links-only html when there is a
 * link). React doesn't own the children, so typing never loses the caret.
 */
export function NoteText({
  block,
  className,
  onChange,
  onLinkKey,
}: {
  block: MarginNoteBlock
  className: string
  onChange: (text: string, html: string | undefined) => void
  /** ⌘K / Ctrl+K in the words: open the link editor on the selection. True when it did. */
  onLinkKey?: () => boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const shown = useRef<string | null>(null)
  const markup = block.html ? noteHtml(block.html) : noteTextHtml(block.text)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || shown.current === markup) return
    el.innerHTML = markup
    shown.current = markup
  }, [markup])
  const own = !!block.own
  const emit = () => {
    const el = ref.current
    if (!el) return
    // Chrome keeps a <br> in an emptied box, which would hide the placeholder.
    if (!el.textContent && el.querySelector('br')) el.innerHTML = ''
    let raw = el.innerHTML
    // A trailing <br> is the browser's placeholder for the empty last line, not a line of its own.
    if (el.lastChild?.nodeName === 'BR') raw = raw.replace(/<br>$/, '')
    const html = noteHtml(raw)
    const text = noteHtmlText(html)
    const linked = noteHasLink(html)
    shown.current = linked ? html : noteTextHtml(text)
    onChange(text, linked ? html : undefined)
  }
  return (
    <div
      ref={ref}
      data-note-text={block.id}
      role={own ? 'textbox' : undefined}
      aria-multiline={own ? true : undefined}
      aria-label={own ? 'Note text' : undefined}
      contentEditable={own}
      suppressContentEditableWarning
      data-ph={own ? 'Your words…' : undefined}
      spellCheck={own}
      onInput={emit}
      onClick={(e) => {
        e.stopPropagation()
        // A link in the words never leaves the composer on a plain click; with ⌘/Ctrl held it opens in a new tab.
        const a = (e.target as Element).closest?.('a')
        if (!a) return
        e.preventDefault()
        const href = a.getAttribute('href')
        if ((e.metaKey || e.ctrlKey) && href) window.open(href, '_blank', 'noopener,noreferrer')
      }}
      onPaste={(e) => {
        if (!own) return
        e.preventDefault()
        insertPlainText(e.clipboardData.getData('text/plain'))
      }}
      onKeyDown={(e) => {
        // Typing here must never reach the note's own keys (Backspace removes it).
        e.stopPropagation()
        if (isLinkKey(e)) {
          if (onLinkKey?.()) e.preventDefault()
          return
        }
        const mod = e.metaKey || e.ctrlKey
        // Plain words: no bold, italic or underline.
        if (mod && !e.altKey && ['b', 'i', 'u'].includes(e.key.toLowerCase())) e.preventDefault()
        if (own && e.key === 'Enter' && !e.nativeEvent.isComposing) {
          e.preventDefault()
          document.execCommand('insertLineBreak')
        }
      }}
      className={`m-0 block w-full whitespace-pre-wrap break-words outline-none empty:before:pointer-events-none empty:before:text-muted-foreground/70 empty:before:content-[attr(data-ph)] [&_a]:text-primary [&_a]:underline [&_a]:decoration-primary/40 [&_a]:underline-offset-2 ${className}`}
    />
  )
}

/** "Burrow · A thinking space", the source's title — or, unsourced and not the author's, a prompt to add one. */
export function SourceNote({ block, onAdd, className = '' }: { block: ThoughtLikeBlock; onAdd: () => void; className?: string }) {
  const hasSource = !!(block.source?.title || block.source?.url)
  if (!hasSource && !block.own) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onAdd()
        }}
        className="self-start text-left font-sans text-[11px] font-medium text-accent hover:underline"
      >
        No source yet — add one, or mark it as yours
      </button>
    )
  }
  const label = thoughtSourceLabel(block)
  return (
    <span className={`font-sans text-[11px] font-medium ${className}`}>
      {block.source?.url ? (
        <a
          href={block.source.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-inherit no-underline hover:underline"
        >
          {label}
        </a>
      ) : (
        label
      )}
    </span>
  )
}

/** A row of small text actions under a selected margin item (the dark toolbar is too wide for a margin). */
export function AsideActions({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      role="toolbar"
      aria-label={label}
      data-keep-selection
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => e.stopPropagation()}
      className="flex flex-wrap items-center gap-x-3 gap-y-1 font-sans text-[11.5px] font-medium text-muted-foreground"
    >
      {children}
    </div>
  )
}

export function AsideAction({ onClick, children, danger }: { onClick: () => void; children: ReactNode; danger?: boolean }) {
  return (
    <button
      type="button"
      // The danger action is Remove, which Backspace or Delete also does to the selected card.
      aria-keyshortcuts={danger ? 'Backspace Delete' : undefined}
      title={danger ? 'Remove (Backspace)' : undefined}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={`whitespace-nowrap hover:underline ${danger ? 'hover:text-primary' : 'hover:text-foreground'}`}
    >
      {children}
    </button>
  )
}

export function SourceForm({
  initial,
  own,
  onCancel,
  onSave,
}: {
  initial?: Source
  own: boolean
  onCancel: () => void
  onSave: (source: Source | undefined, own: boolean) => void
}) {
  const [title, setTitle] = useState(initial?.title ?? '')
  const [url, setUrl] = useState(initial?.url ?? '')
  const [mine, setMine] = useState(own)
  return (
    <form
      data-keep-selection
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation()
        if (e.key === 'Escape') onCancel()
      }}
      onSubmit={(e) => {
        e.preventDefault()
        const raw = url.trim()
        const link = raw && !/^https?:\/\//i.test(raw) ? `https://${raw}` : raw
        const t = title.trim()
        onSave(t || link ? { title: t || undefined, url: link || undefined } : undefined, mine)
      }}
      className="relative z-10 mt-1 flex flex-col gap-2.5 rounded-lg border border-border/60 bg-card p-3 font-sans"
    >
      <label className="flex flex-col gap-1">
        <span className={labelCls}>SOURCE</span>
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Article, book or person" className={inputCls} />
      </label>
      <label className="flex flex-col gap-1">
        <span className={labelCls}>LINK</span>
        <input value={url} onChange={(e) => setUrl(e.target.value)} inputMode="url" placeholder="https://" className={inputCls} />
      </label>
      <label className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} className="accent-wine" />
        This is my own thought — no source needed
      </label>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={onCancel} className={btn.ghost}>
          Cancel
        </button>
        <button type="submit" className={btn.primary}>
          Save
        </button>
      </div>
    </form>
  )
}
